/**
 * Analytics Dashboard Component for Admin Panel
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  BarChart3, 
  Eye, 
  Search, 
  Download, 
  Users, 
  TrendingUp,
  RefreshCw,
  Calendar,
  Globe,
  FileSpreadsheet
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#0046E8', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const AnalyticsDashboard = ({ token }) => {
  const [summary, setSummary] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [error, setError] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, activityRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/summary?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/analytics/recent-activity?limit=20&exclude_admins=true`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (!summaryRes.ok || !activityRes.ok) {
        throw new Error('Error fetching analytics');
      }

      const summaryData = await summaryRes.json();
      const activityData = await activityRes.json();

      setSummary(summaryData);
      setRecentActivity(activityData.events || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, days]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatEventType = (type) => {
    const types = {
      page_view: 'Vista de página',
      product_view: 'Vista de producto',
      search: 'Búsqueda',
      filter: 'Filtro',
      download: 'Descarga Excel',
      login: 'Login',
      logout: 'Logout',
      click: 'Click',
      error: 'Error',
    };
    return types[type] || type;
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    const date = new Date(ts);
    return date.toLocaleString('es-ES', { 
      day: '2-digit', 
      month: '2-digit', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500 mb-4">{error}</p>
        <Button onClick={fetchAnalytics} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <BarChart3 className="w-6 h-6" />
          Analíticas
          <span className="text-sm font-normal text-gray-500">(excluye admins)</span>
        </h2>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {[7, 14, 30, 90].map((d) => (
              <Button
                key={d}
                variant={days === d ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setDays(d)}
              >
                {d}d
              </Button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={fetchAnalytics}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards - Extended with Downloads */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Eventos</p>
                <p className="text-2xl font-bold">{summary?.total_events?.toLocaleString() || 0}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Visitantes Únicos</p>
                <p className="text-2xl font-bold">{summary?.unique_visitors?.toLocaleString() || 0}</p>
              </div>
              <Users className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Vistas de Página</p>
                <p className="text-2xl font-bold">{summary?.page_views?.toLocaleString() || 0}</p>
              </div>
              <Eye className="w-8 h-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Búsquedas</p>
                <p className="text-2xl font-bold">{summary?.searches?.toLocaleString() || 0}</p>
              </div>
              <Search className="w-8 h-8 text-orange-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-emerald-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-700">Descargas Excel</p>
                <p className="text-2xl font-bold text-emerald-700">{summary?.downloads?.toLocaleString() || 0}</p>
              </div>
              <FileSpreadsheet className="w-8 h-8 text-emerald-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Events Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Eventos por Día
            </CardTitle>
          </CardHeader>
          <CardContent>
            {summary?.daily_events?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={summary.daily_events}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(val) => val?.slice(5) || val}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#0046E8" 
                    strokeWidth={2}
                    dot={{ fill: '#0046E8' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-500 text-center py-8">Sin datos</p>
            )}
          </CardContent>
        </Card>

        {/* Events by Type Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Eventos por Tipo
            </CardTitle>
          </CardHeader>
          <CardContent>
            {summary?.events_by_type?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={summary.events_by_type}
                    dataKey="count"
                    nameKey="type"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ type, count }) => `${formatEventType(type)}: ${count}`}
                  >
                    {summary.events_by_type.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [value, formatEventType(name)]} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-500 text-center py-8">Sin datos</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Lists Row - Including Countries */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Top Countries - NEW */}
        <Card className="bg-blue-50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-600" />
              Top Países
            </CardTitle>
          </CardHeader>
          <CardContent>
            {summary?.top_countries?.length > 0 ? (
              <ul className="space-y-2">
                {summary.top_countries.slice(0, 8).map((country, idx) => (
                  <li key={idx} className="flex justify-between items-center text-sm">
                    <span className="truncate max-w-[120px]" title={country.country}>
                      {idx + 1}. {country.country || 'Desconocido'}
                    </span>
                    <span className="font-semibold text-blue-600">{country.visits?.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-sm text-center py-4">Sin datos de país aún</p>
            )}
          </CardContent>
        </Card>

        {/* Top Pages */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Top Páginas</CardTitle>
          </CardHeader>
          <CardContent>
            {summary?.top_pages?.length > 0 ? (
              <ul className="space-y-2">
                {summary.top_pages.slice(0, 6).map((page, idx) => (
                  <li key={idx} className="flex justify-between items-center text-sm">
                    <span className="truncate max-w-[120px]" title={page.page}>
                      {page.page || 'N/A'}
                    </span>
                    <span className="font-semibold text-blue-600">{page.views}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-sm">Sin datos</p>
            )}
          </CardContent>
        </Card>

        {/* Top Products */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Top Productos</CardTitle>
          </CardHeader>
          <CardContent>
            {summary?.top_products?.length > 0 ? (
              <ul className="space-y-2">
                {summary.top_products.slice(0, 6).map((product, idx) => (
                  <li key={idx} className="flex justify-between items-center text-sm">
                    <span className="truncate max-w-[120px]" title={product.name}>
                      {product.name || 'N/A'}
                    </span>
                    <span className="font-semibold text-green-600">{product.views}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-sm">Sin datos</p>
            )}
          </CardContent>
        </Card>

        {/* Top Searches */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Top Búsquedas</CardTitle>
          </CardHeader>
          <CardContent>
            {summary?.top_searches?.length > 0 ? (
              <ul className="space-y-2">
                {summary.top_searches.slice(0, 6).map((search, idx) => (
                  <li key={idx} className="flex justify-between items-center text-sm">
                    <span className="truncate max-w-[120px]" title={search.query}>
                      "{search.query}"
                    </span>
                    <span className="font-semibold text-orange-600">{search.count}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-sm">Sin datos</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Download Stats - NEW */}
      {summary?.download_stats?.length > 0 && (
        <Card className="bg-emerald-50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Download className="w-5 h-5 text-emerald-600" />
              Estadísticas de Descargas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {summary.download_stats.map((stat, idx) => (
                <div key={idx} className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-sm text-gray-500 uppercase">{stat.type || 'Total'}</p>
                  <p className="text-2xl font-bold text-emerald-700">{stat.count}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Actividad Reciente (Clientes)</CardTitle>
        </CardHeader>
        <CardContent>
          {recentActivity.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2">Hora</th>
                    <th className="text-left py-2 px-2">Tipo</th>
                    <th className="text-left py-2 px-2">Detalle</th>
                    <th className="text-left py-2 px-2">País</th>
                    <th className="text-left py-2 px-2">Sesión</th>
                  </tr>
                </thead>
                <tbody>
                  {recentActivity.map((event, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-2 text-gray-500">
                        {formatTimestamp(event.timestamp)}
                      </td>
                      <td className="py-2 px-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          event.event_type === 'page_view' ? 'bg-purple-100 text-purple-700' :
                          event.event_type === 'product_view' ? 'bg-green-100 text-green-700' :
                          event.event_type === 'search' ? 'bg-orange-100 text-orange-700' :
                          event.event_type === 'download' ? 'bg-emerald-100 text-emerald-700' :
                          event.event_type === 'login' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {formatEventType(event.event_type)}
                        </span>
                      </td>
                      <td className="py-2 px-2 truncate max-w-[200px]">
                        {event.page || event.product_name || event.search_query || event.download_type || '-'}
                      </td>
                      <td className="py-2 px-2 text-sm">
                        {event.country ? (
                          <span className="inline-flex items-center gap-1">
                            <Globe className="w-3 h-3 text-blue-500" />
                            {event.country}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="py-2 px-2 text-gray-400 text-xs font-mono">
                        {event.session_id?.slice(0, 10) || '-'}...
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">Sin actividad reciente</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsDashboard;
